// Fechas de negocio (§6): se muestran y escriben como dd/mm/aaaa, se guardan como ISO yyyy-mm-dd y
// "Hoy" se calcula en America/Guayaquil, sin depender de la zona horaria del dispositivo.

export const TIME_ZONE = 'America/Guayaquil';
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

export const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** Semana de lunes a domingo. */
export const WEEKDAYS = [
  { short: 'lu', long: 'lunes' },
  { short: 'ma', long: 'martes' },
  { short: 'mi', long: 'miércoles' },
  { short: 'ju', long: 'jueves' },
  { short: 'vi', long: 'viernes' },
  { short: 'sá', long: 'sábado' },
  { short: 'do', long: 'domingo' },
] as const;

const isoFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat('es-EC', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Fecha ISO de hoy en Guayaquil. */
export function todayIso(now: Date = new Date()): string {
  return isoFormatter.format(now);
}

export function isValidIsoDate(iso: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return (
    year >= MIN_YEAR && year <= MAX_YEAR && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month)
  );
}

export type ParsedDate = { ok: true; iso: string } | { ok: false; error: string };

/** Interpreta dd/mm/aaaa (acepta d/m/aaaa y guiones). Rechaza días inexistentes sin normalizar. */
export function parseDisplayDate(text: string): ParsedDate {
  const value = text.trim();
  if (value === '') return { ok: false, error: 'Ingresa la fecha.' };
  const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value);
  if (!match) return { ok: false, error: 'Usa el formato dd/mm/aaaa.' };
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < MIN_YEAR || year > MAX_YEAR)
    return { ok: false, error: `El año debe estar entre ${MIN_YEAR} y ${MAX_YEAR}.` };
  if (month < 1 || month > 12) return { ok: false, error: 'El mes no existe.' };
  if (day < 1 || day > daysInMonth(year, month)) return { ok: false, error: 'Ese día no existe en el calendario.' };
  return { ok: true, iso: `${year}-${pad(month)}-${pad(day)}` };
}

/**
 * Da formato dd/mm/aaaa mientras se escribe: solo deja dígitos y separa día, mes y año con "/".
 * Cualquier separador (guion, espacio, punto) cuenta como "/", y un día o mes de una cifra seguido de
 * separador se completa con cero: "16-10-2026", "16 10 2026" y "16102026" quedan "16/10/2026";
 * "1/3/2026", "01/03/2026". También acepta aaaa-mm-dd pegado. La barra se agrega al llegar el dígito
 * siguiente ("161" → "16/1"), para que borrar con retroceso no se trabe en ella.
 */
export function maskDisplayDate(text: string): string {
  const yearFirst = /^\s*(\d{4})\D+(\d{1,2})\D+(\d{1,2})\s*$/.exec(text);
  if (yearFirst) {
    const [, year = '', month = '', day = ''] = yearFirst;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  const parts = [''];
  for (const char of text) {
    const index = parts.length - 1;
    const part = parts[index] ?? '';
    if (/\d/.test(char)) {
      if (part.length < (index < 2 ? 2 : 4)) parts[index] = part + char;
      else if (index < 2) parts.push(char);
      else break;
    } else if (/[\s/.\-,_]/.test(char) && part !== '' && index < 2) {
      if (/^[1-9]$/.test(part)) parts[index] = `0${part}`;
      parts.push('');
    }
  }
  return parts.join('/');
}

/** ISO yyyy-mm-dd → dd/mm/aaaa. Devuelve el texto original si no es una fecha ISO. */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

/**
 * Marca de tiempo → dd/mm/aaaa hh:mm a.m./p.m. en Guayaquil (reloj de 12 horas, hora con dos cifras):
 * 12/10/2026 10:30 p.m., 24/10/2026 01:12 a.m. Entre la hora y "a.m."/"p.m." va un espacio duro, para que
 * nunca queden en líneas distintas.
 */
export function formatDateTime(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  const parts = Object.fromEntries(dateTimeFormatter.formatToParts(new Date(timestamp)).map((p) => [p.type, p.value]));
  const hour = Number(parts['hour']);
  const period = hour < 12 ? 'a.m.' : 'p.m.';
  return `${parts['day']}/${parts['month']}/${parts['year']} ${pad(hour % 12 || 12)}:${parts['minute']}\u00a0${period}`;
}

const isoDateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'longOffset',
});

/** Instante en ISO 8601 con la hora de Guayaquil y su desfase: 2026-10-20T13:29:58-05:00. */
export function formatIsoDateTime(date: Date = new Date()): string {
  const parts = Object.fromEntries(isoDateTimeFormatter.formatToParts(date).map((p) => [p.type, p.value]));
  const offset = (parts['timeZoneName'] ?? '').replace('GMT', '') || '+00:00';
  return `${parts['year']}-${parts['month']}-${parts['day']}T${parts['hour']}:${parts['minute']}:${parts['second']}${offset}`;
}

/** Fecha y hora de Guayaquil para nombres de archivo: 2026-10-20-13-29-58. */
export function fileStamp(date: Date = new Date()): string {
  return formatIsoDateTime(date).slice(0, 19).replace(/[:T]/g, '-');
}

export interface CalendarDay {
  iso: string;
  day: number;
  inMonth: boolean;
}

/** Semanas (lunes a domingo) que cubren el mes indicado (month 1–12). */
export function monthGrid(year: number, month: number): CalendarDay[][] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const offset = (first.getUTCDay() + 6) % 7; // lunes = 0
  const start = new Date(Date.UTC(year, month - 1, 1 - offset));
  const weeks: CalendarDay[][] = [];
  const cursor = new Date(start);
  do {
    const week: CalendarDay[] = [];
    for (let i = 0; i < 7; i++) {
      week.push({
        iso: `${cursor.getUTCFullYear()}-${pad(cursor.getUTCMonth() + 1)}-${pad(cursor.getUTCDate())}`,
        day: cursor.getUTCDate(),
        inMonth: cursor.getUTCMonth() === month - 1,
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push(week);
  } while (cursor.getUTCMonth() === month - 1);
  return weeks;
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}
